import { Attribute } from '../../decorators/attribute.decorator';
import { Component } from '../../decorators/component.decorator';
import { formatCurrencyValue } from '../../global/helpers';
import type { NullOr } from '../../global/utils.type';
import { UIElement } from '../ui-element';
import type { AttributeValue, ObservedAttributes } from '../ui-element.type';
import { UICurrencyLayout, UICurrencyLayouts } from './ui-currency.type';
import styles from './ui-currency.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UICurrency
 * @element ui-currency
 * @classdesc Represents a class for <code>ui-currency</code> element.
 * Formats currency value.
 * @property {number} [value=0] {@attr value} raw value
 * @property {string} [separator] {@attr separator} separate groups of thousands
 * @property {number} [accuracy] {@attr accuracy} amount of decimals numbers after comma
 * @property {UICurrencyLayouts} [layout] {@attr layout} layout of the currency display
 * @example
 * <ui-currency value="12345678.98"></ui-currency
 * @example
 * <ui-currency value="12345678.98" accuracy="0" separator=","></ui-currency>
 */
@Component({
    tag: 'ui-currency',
    styles,
})
export class UICurrency extends UIElement {
    /**
     * @type {Array<string>}
     */
    static get observedAttributes(): ObservedAttributes {
        return ['value', 'separator', 'accuracy', 'layout'];
    }

    static readonly Layout: UICurrencyLayouts = {
        Result: 'result',
    };

    @Attribute(String) layout?: UICurrencyLayout;
    @Attribute(Number) value: number = 0;
    @Attribute(String) separator?: string;
    @Attribute(Number) accuracy: number = 2;

    updateContent(): void {
        if (this.hasAttribute('value') || !this.innerHTML) {
            const formattedCurrencyValue = formatCurrencyValue(
                this.value || 0,
                this.separator || ' ',
                this.accuracy
            );
            this.setInnerText(formattedCurrencyValue ?? '');
        }
    }

    /**
     * @inheritDoc
     */
    override observeAttributes(
        name: AttributeValue,
        oldValue: AttributeValue,
        newValue: NullOr<string>
    ): void {
        /* istanbul ignore if */
        if (!this.hydrated) {
            return;
        }
        this.updateContent();
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        this.updateContent();
    }
}
